import { ILenderCreditRules } from '../components/DealCalculatorLenders/components/LenderSection/LenderCreditRules';
import { IInputs, InputsModel } from './InputsModel';
import { ITierCondition, TierConditionModel } from './TierConditionModel';
import { IPrimaryBorrowerBureau, PrimaryBorrowerBureauModel } from './PrimaryBorrowerBureauModel';
import { IProducts, ProductsModel } from './ProductsRetail';

export interface IEligibleLender {
  id: string;
  numericId: number;
  name: string;
  code: string;
  defaultValuationMethods: string[];
  daysToFirstPaymentGreen: number;
  daysToFirstPaymentYellow?: number;
  maxDtiGreen: number;
  maxDtiYellow?: number;
  maxPtiGreen: number;
  maxPtiYellow?: number;
  rank: number;
  subRank?: number;
  availableMarkupIncrements: string;
  maxMileageGreen: number;
  maxMileageYellow?: number;
  maxAgeGreen: number;
  maxAgeYellow?: number;
  minPrimaryFicoGreen: number;
  minPrimaryFicoYellow?: number;
  approvalDaysGreen?: number;
  approvalDaysYellow?: number;
  maxCashOutGreen: number;
  maxCashOutYellow?: number;
  maxFlatPercentGreen: number;
  maxFlatPercentYellow?: number;
  maxGapGreen: number;
  maxGapYellow?: number;
  ltvType: string;
  maxFlatGreen?: number;
  maxFlatYellow?: number;
  flatPercent?: number;
  maxOtherProducts?: number;
  maxProduct?: number;
  maxBackEndProduct: number;
  fundingFeeGreen: number;
  fundingFeeYellow?: number;
  primaryBorrowerBureau: Partial<IPrimaryBorrowerBureau>;
  coContactId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  coBorrowerBureau?: Partial<IPrimaryBorrowerBureau>;
  maxVSC?: number;
  age: number;
  mileage: number;
  valuationUsed: string;
  valuation?: number;
  achDiscount?: number;
  isOpenLending: boolean;
  isDefi: boolean;
  totalDealFeeAmount: number;
  autoSubmitLoanApplication: boolean;
  useAprBasedCalculation: boolean;
  autoSubmitSelectedTierCondition?: IAutoSubmitSelectedTierCondition;
  contractDateForUsingAprBasedCalculations?: Date;
  isRestrictDr: boolean;
  daysToFirstPaymentForContactState?: number;
  tierConditions: Partial<ITierCondition>[];
  inputs: Partial<IInputs>;
  products: Partial<IProducts>;
  lenderCreditRules?: ILenderCreditRules[];
  actualDti?: number;
  isDeclined?: boolean;
}

export interface IAutoSubmitSelectedTierCondition extends ITierCondition {
  pti: number;
  requiredDownPaymentGreen: number;
}

export class EligibleLender implements Partial<IEligibleLender> {
  id?: string;
  numericId?: number;
  name?: string;
  code?: string;
  defaultValuationMethods?: string[];
  daysToFirstPaymentGreen?: number;
  daysToFirstPaymentYellow?: number;
  maxDtiGreen?: number;
  maxDtiYellow?: number;
  maxPtiGreen?: number;
  maxPtiYellow?: number;
  rank?: number;
  subRank?: number;
  availableMarkupIncrements?: string;
  maxMileageGreen?: number;
  maxMileageYellow?: number;
  maxAgeGreen?: number;
  maxAgeYellow?: number;
  minPrimaryFicoGreen?: number;
  minPrimaryFicoYellow?: number;
  approvalDaysGreen?: number;
  approvalDaysYellow?: number;
  maxCashOutGreen?: number;
  maxCashOutYellow?: number;
  maxFlatPercentGreen?: number;
  maxFlatPercentYellow?: number;
  maxGapGreen?: number;
  maxGapYellow?: number;
  ltvType?: string;
  maxFlatGreen?: number;
  maxFlatYellow?: number;
  flatPercent?: number;
  maxOtherProducts?: number;
  maxProduct?: number;
  maxBackEndProduct?: number;
  fundingFeeGreen?: number;
  fundingFeeYellow?: number;
  primaryBorrowerBureau?: Partial<IPrimaryBorrowerBureau>;
  coContactId?: string;
  coBorrowerBureau?: Partial<IPrimaryBorrowerBureau>;
  maxVSC?: number;
  age?: number;
  mileage?: number;
  valuationUsed?: string;
  valuation?: number;
  achDiscount?: number;
  isOpenLending?: boolean;
  isDefi?: boolean;
  totalDealFeeAmount?: number;
  autoSubmitLoanApplication?: boolean;
  useAprBasedCalculation?: boolean;
  contractDateForUsingAprBasedCalculations?: Date;
  isRestrictDr?: boolean;
  daysToFirstPaymentForContactState?: number;
  tierConditions?: Partial<ITierCondition>[];
  inputs?: Partial<IInputs>;
  products?: Partial<IProducts>;
  lenderCreditRules?: ILenderCreditRules[];
  actualDti?: number;
  autoSubmitSelectedTierCondition?: IAutoSubmitSelectedTierCondition;

  constructor(data?: IEligibleLender) {
    if (data) {
      this.id = data?.id;
      this.numericId = data?.numericId;
      this.name = data?.name;
      this.code = data?.code;
      this.defaultValuationMethods = data?.defaultValuationMethods;
      this.daysToFirstPaymentGreen = data?.daysToFirstPaymentGreen;
      this.daysToFirstPaymentYellow = data?.daysToFirstPaymentYellow;
      this.maxDtiGreen = data?.maxDtiGreen;
      this.maxDtiYellow = data?.maxDtiYellow;
      this.maxPtiGreen = data?.maxPtiGreen;
      this.maxPtiYellow = data?.maxPtiYellow;
      this.rank = data?.rank;
      this.subRank = data?.subRank;
      this.availableMarkupIncrements = data?.availableMarkupIncrements;
      this.maxMileageGreen = data?.maxMileageGreen;
      this.maxMileageYellow = data?.maxMileageYellow;
      this.maxAgeGreen = data?.maxAgeGreen;
      this.maxAgeYellow = data?.maxAgeYellow;
      this.minPrimaryFicoGreen = data?.minPrimaryFicoGreen;
      this.minPrimaryFicoYellow = data?.minPrimaryFicoYellow;
      this.approvalDaysGreen = data?.approvalDaysGreen;
      this.approvalDaysYellow = data?.approvalDaysYellow;
      this.maxCashOutGreen = data?.maxCashOutGreen;
      this.maxCashOutYellow = data?.maxCashOutYellow;
      this.maxFlatPercentGreen = data?.maxFlatPercentGreen;
      this.maxFlatPercentYellow = data?.maxFlatPercentYellow;
      this.maxGapGreen = data?.maxGapGreen;
      this.maxGapYellow = data?.maxGapYellow;
      this.ltvType = data?.ltvType;
      this.maxFlatGreen = data?.maxFlatGreen;
      this.maxFlatYellow = data?.maxFlatYellow;
      this.flatPercent = data?.flatPercent;
      this.maxOtherProducts = data?.maxOtherProducts;
      this.maxProduct = data?.maxProduct;
      this.maxBackEndProduct = data?.maxBackEndProduct;
      this.fundingFeeGreen = data?.fundingFeeGreen;
      this.fundingFeeYellow = data?.fundingFeeYellow;
      this.primaryBorrowerBureau = new PrimaryBorrowerBureauModel(data?.primaryBorrowerBureau);
      this.coContactId = data?.coContactId;
      this.coBorrowerBureau = data?.coBorrowerBureau;
      this.maxVSC = data?.maxVSC;
      this.age = data?.age;
      this.mileage = data?.mileage;
      this.valuationUsed = data?.valuationUsed;
      this.valuation = data?.valuation;
      this.achDiscount = data?.achDiscount;
      this.isOpenLending = data?.isOpenLending;
      this.isDefi = data?.isDefi;
      this.totalDealFeeAmount = data?.totalDealFeeAmount;
      this.autoSubmitLoanApplication = data?.autoSubmitLoanApplication;
      this.useAprBasedCalculation = data?.useAprBasedCalculation;
      this.contractDateForUsingAprBasedCalculations = data?.contractDateForUsingAprBasedCalculations;
      this.isRestrictDr = data?.isRestrictDr;
      this.daysToFirstPaymentForContactState = data?.daysToFirstPaymentForContactState;
      this.tierConditions = data?.tierConditions?.map((i) => new TierConditionModel(i));
      this.inputs = new InputsModel(data?.inputs);
      this.products = new ProductsModel(data?.products);
      this.lenderCreditRules = data.lenderCreditRules;
      this.actualDti = data?.actualDti;
      this.autoSubmitSelectedTierCondition = data?.autoSubmitSelectedTierCondition;
    }
  }
}
